import { useState } from 'react';
import type { JSX } from 'react';
import type { AiFocus, JournalQuestData, Remark, RemarkLink, RemarkSettings } from '@roll1/shared';
import { remarksCount } from '@roll1/shared';
import type { RemarkInput, RemarksView } from '@roll1/shared';
import { toast } from 'sonner';
import { useI18n } from '../../../i18n';
import { apiFetch } from '../../../lib/api';
import { announceRemarksFound, requestAiAsk, requestApplyHint, requestRemarkFix } from '../../../lib/aiBridge';
import { useRemarks } from '../../../hooks/useRemarks';
import { useSessionContext } from '../../../contexts/SessionContext';
import ConfirmDialog from '../../ConfirmDialog';
import { fill, fixLabel, remarkBody, remarkTitle } from './remarkText';
import { Checkbox } from '../../ui/checkbox';

/* Mockup: studio-ai-chat-preview-v2.html — viewCheck() / .find / .settings / .pill.guessk / .btn / .btn.ok / .btn.ghost */
import { BTN, BTN_OK, BTN_GHOST, PILL_GUESS } from '../../ui/vocab';
const FIND = 'flex flex-col gap-1.5 rounded-md border border-line bg-panel-2 px-2.5 py-2';

/** Where a remark's request is about: its first piece of evidence. */
export function focusOfLink(link: RemarkLink | undefined): AiFocus | undefined {
  if (link === undefined) return undefined;
  return { place: 'studio', kind: link.kind, id: link.id };
}

const SWITCHES: ReadonlyArray<{
  key: keyof RemarkSettings;
  label: 'remarks.sw.game' | 'remarks.sw.prep' | 'remarks.sw.fixes' | 'remarks.sw.simple';
  hint: 'remarks.sw.gameHint' | 'remarks.sw.prepHint' | 'remarks.sw.fixesHint' | 'remarks.sw.simpleHint';
  model: boolean;
}> = [
  { key: 'gameHints', label: 'remarks.sw.game', hint: 'remarks.sw.gameHint', model: true },
  { key: 'prepHints', label: 'remarks.sw.prep', hint: 'remarks.sw.prepHint', model: true },
  { key: 'offerFixes', label: 'remarks.sw.fixes', hint: 'remarks.sw.fixesHint', model: false },
  { key: 'simpleChecks', label: 'remarks.sw.simple', hint: 'remarks.sw.simpleHint', model: false },
];

export interface RemarksPanelProps {
  readonly view: RemarksView;
  readonly input: RemarkInput;
  /** Open the thing a link names in Studio. */
  readonly onOpenLink: (link: RemarkLink) => void;
}

export function RemarksPanel({ view, input, onOpenLink }: RemarksPanelProps): JSX.Element {
  const { t } = useI18n();
  const { settings, setSetting, dismiss } = useRemarks();
  const { mp } = useSessionContext();
  const [configuring, setConfiguring] = useState(false);
  const [confirm, setConfirm] = useState<Remark | undefined>(undefined);
  const [checking, setChecking] = useState(false);
  const sessionId = mp.session?.id;

  const kindOf = (id: string): 'quest' | 'record' => (input.journal.find(e => e.id === id)?.type === 'quest' ? 'quest' : 'record');
  const title = (r: Remark): string => remarkTitle(r, t, kindOf);
  const focusOf = (r: Remark): { focus: AiFocus } | Record<string, never> => {
    const focus = focusOfLink(r.links[0]);
    return focus === undefined ? {} : { focus };
  };

  const discuss = (r: Remark): void => {
    requestAiAsk({ message: t('remarks.discussMsg'), ...focusOf(r), topic: { remarkKey: r.key, title: title(r) } });
  };

  const fix = (r: Remark): void => {
    if (r.kind === 'simple' && r.check === 'empty-prep') {
      requestAiAsk({ message: fill(t('remarks.ask.prep'), { beat: r.links[0]?.name ?? '' }), ...focusOf(r) });
      return;
    }
    if (r.kind === 'simple' && r.check === 'empty-npc') {
      requestAiAsk({ message: fill(t('remarks.ask.npc'), { npc: r.links[0]?.name ?? '' }), ...focusOf(r) });
      return;
    }
    if (r.kind !== 'simple') {
      if (r.fixPrompt !== undefined && r.fixPrompt !== '') requestAiAsk({ message: r.fixPrompt, ...focusOf(r) });
      return;
    }
    if (r.fix?.confirm !== undefined) { setConfirm(r); return; }
    requestRemarkFix({ key: r.key, title: title(r) });
  };

  const checkPrep = async (): Promise<void> => {
    if (sessionId === undefined || checking) return;
    setChecking(true);
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/remarks`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (!res.ok) { toast.error(t('remarks.checkPrepFailed')); return; }
      const data = (await res.json().catch(() => ({}))) as { count?: number };
      if (typeof data.count === 'number' && data.count > 0) announceRemarksFound(data.count);
    } catch {
      toast.error(t('remarks.checkPrepFailed'));
    } finally {
      setChecking(false);
    }
  };

  /** «квест «Лошади»», «бит «Врата»»: the kind of the thing before its name (mockup .ev). */
  const linkText = (l: RemarkLink): string => {
    const key =
      l.kind === 'beat' ? 'remarks.lk.beat'
      : l.kind === 'page' ? 'remarks.lk.page'
      : l.kind === 'sheet' ? (input.sheets.find(x => x.id === l.id)?.sheetType === 'npc' ? 'remarks.lk.npc' : 'remarks.lk.sheet')
      : (kindOf(l.id) === 'quest' ? 'remarks.lk.quest' : 'remarks.lk.record');
    return fill(t(key), { name: l.name });
  };

  const evidence = (r: Remark): JSX.Element | null => r.links.length === 0 ? null : (
    <div className="flex flex-wrap items-center gap-1.5 text-ui text-muted-foreground">
      {r.links.flatMap((l, i): JSX.Element[] => [
        ...(i > 0 ? [<span key={`dot-${i}`}>·</span>] : []),
        <button key={`${l.kind}:${l.id}`} type="button" className="text-ui text-hl hover:underline" onClick={(): void => onOpenLink(l)}>{linkText(l)}</button>,
      ])}
    </div>
  );

  const objectiveName = (r: Remark): string => {
    const quest = input.journal.find(e => e.id === r.questId);
    return ((quest?.data as JournalQuestData | undefined)?.objectives ?? []).find(o => o.id === r.objectiveId)?.text ?? '';
  };

  const gameCard = (r: Remark): JSX.Element => (
    <div key={r.key} className={FIND} data-testid="remark" data-kind="game" data-key={r.key}>
      <div className="flex flex-wrap items-center gap-2">
        <strong className="text-body-s text-fg">{r.title}</strong>
        <span className={PILL_GUESS}>{t('remarks.pill.game')}</span>
      </div>
      <span className="text-body-s text-muted-foreground">
        {fill(t('remarks.game.body'), { obj: objectiveName(r), quest: input.journal.find(e => e.id === r.questId)?.name ?? r.links[0]?.name ?? '' })}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={BTN_OK} onClick={(): void => { requestApplyHint({ key: r.key, title: r.title }); }}>{t('remarks.mark')}</button>
        <button type="button" className={BTN} onClick={(): void => discuss(r)}>{t('remarks.discuss')}</button>
        <button type="button" className={BTN_GHOST} onClick={(): void => dismiss(r.key)}>{t('remarks.no')}</button>
      </div>
    </div>
  );

  const remarkCard = (r: Remark): JSX.Element => {
    const hasFix = r.fix !== undefined && settings.offerFixes && (r.kind === 'simple' || (r.fixPrompt ?? '') !== '');
    return (
      <div key={r.key} className={FIND} data-testid="remark" data-kind={r.kind} data-key={r.key}>
        <div className="flex flex-wrap items-center gap-2">
          <strong className="text-body-s text-fg">{title(r)}</strong>
          {r.kind === 'prep' ? <span className={PILL_GUESS}>{t('remarks.pill.opinion')}</span> : null}
        </div>
        <span className="text-body-s text-muted-foreground">{remarkBody(r, t)}</span>
        {evidence(r)}
        <div className="flex flex-wrap items-center gap-2">
          {hasFix ? <button type="button" className={BTN_OK} onClick={(): void => fix(r)}>{fixLabel(r, t)}</button> : null}
          <button type="button" className={BTN} onClick={(): void => discuss(r)}>{t('remarks.discuss')}</button>
          <button type="button" className={BTN_GHOST} onClick={(): void => dismiss(r.key)}>{r.fix !== undefined ? t('remarks.leave') : t('remarks.asIntended')}</button>
        </div>
      </div>
    );
  };

  const total = remarksCount(view);
  return (
    <section role="region" aria-label={t('remarks.heading')} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3" data-testid="remarks-panel">
      <div className="flex items-center gap-2">
        <h1 className="text-title font-semibold text-fg">{t('remarks.heading')}</h1>
        <button type="button" className={`${BTN_GHOST} ml-auto`} aria-expanded={configuring} onClick={(): void => setConfiguring(v => !v)}>{t('remarks.configure')}</button>
      </div>
      {configuring ? (
        <div className="flex flex-col gap-2 rounded-md border border-line bg-panel-2 px-2.5 py-2" data-testid="remarks-settings">
          <span className="text-meta text-dim">{t('remarks.settings.title')}</span>
          {SWITCHES.map((sw) => (
            <label key={sw.key} className="flex items-start gap-2 text-body-s">
              <Checkbox className="mt-0.5" checked={settings[sw.key]} onCheckedChange={(value): void => setSetting(sw.key, value === true)} />
              <span className="flex flex-col">
                <b className="font-medium text-fg">{t(sw.label)}</b>
                <span className="text-meta text-dim">{t(sw.hint)}{sw.model && !settings[sw.key] ? t('remarks.sw.off') : ''}</span>
              </span>
            </label>
          ))}
        </div>
      ) : null}
      <span className="text-meta text-dim">{t('remarks.hint')}</span>
      {view.game.map(gameCard)}
      {view.open.map(remarkCard)}
      {view.staleGame.map((r) => (
        <div key={r.key} className="text-meta text-dim" data-testid="remark-stale">{t('remarks.pill.gameStale')} · {r.title}</div>
      ))}
      {total === 0 ? <span className="text-meta text-dim">{t('remarks.empty')}</span> : null}
      {view.leftAsIs > 0 ? <span className="text-meta text-dim">{fill(t('remarks.leftAsIs'), { n: view.leftAsIs })}</span> : null}
      <span className="text-meta text-dim">{t('remarks.footer')}</span>
      {settings.prepHints ? (
        <div>
          <button type="button" className={BTN} disabled={checking || sessionId === undefined} onClick={(): void => { void checkPrep(); }}>{checking ? t('remarks.checkPrepBusy') : t('remarks.checkPrep')}</button>
        </div>
      ) : null}
      <ConfirmDialog
        open={confirm !== undefined}
        onOpenChange={(open: boolean): void => { if (!open) setConfirm(undefined); }}
        title={confirm === undefined ? '' : fill(t('remarks.confirm.delete'), { a: confirm.links[0]?.name ?? '', b: confirm.links[1]?.name ?? '' })}
        tone="danger"
        description=""
        confirmLabel={t('remarks.confirm.deleteBtn')}
        onConfirm={(): void => { if (confirm !== undefined) requestRemarkFix({ key: confirm.key, title: title(confirm) }); setConfirm(undefined); }}
        onCancel={(): void => setConfirm(undefined)}
      />
    </section>
  );
}
